import {
  LogIn,
  SigninRedirect,
} from "../js/authentication/authentication";
import { authService } from "../js/authentication/authentication";

function getCookieValue(cName) {
  const name = cName + "=";
  const cDecoded = decodeURIComponent(document.cookie); //to be careful
  const cArr = cDecoded.split("; ");
  let res;
  cArr.forEach((val) => {
    if (val.indexOf(name) === 0) res = val.substring(name.length);
  });
  return res;
}

/**
 * Decode JWT payload. Access tokens often include `ixp_market_admin` while the
 * id_token-backed `user.profile` in oidc-client may not — use for permission checks.
 */
function parseJwtPayload(token) {
  if (!token || typeof token !== "string") return null;
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "===".slice(0, (4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (e) {
    return null;
  }
}

function claimContainsMarketId(claimValue, marketId) {
  if (!claimValue || !marketId) return false;
  const target = String(marketId).trim().toLowerCase();

  if (typeof claimValue === "string") {
    const raw = claimValue.trim();
    if (raw.toLowerCase() === target) return true;
    const tokens = raw
      .split(/[\s,;"']+/)
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    return tokens.includes(target);
  }

  if (Array.isArray(claimValue)) {
    return claimValue.some((v) => String(v).trim().toLowerCase() === target);
  }

  if (claimValue && typeof claimValue === "object") {
    const flag = claimValue[marketId] ?? claimValue[String(marketId)];
    if (flag === true || flag === "true") return true;
  }

  return false;
}

function claimContainsRole(claimValue, roleName) {
  if (!claimValue || !roleName) return false;
  const target = String(roleName).trim().toLowerCase();

  if (typeof claimValue === "string") {
    const raw = claimValue.trim().toLowerCase();
    if (raw === target) return true;
    const tokens = raw
      .split(/[\s,;"']+/)
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    return tokens.includes(target);
  }

  if (Array.isArray(claimValue)) {
    return claimValue.some((v) => String(v).trim().toLowerCase() === target);
  }

  return false;
}


export default {
  namespaced: true,

  state: {
    loggedIn: false,
    userName: "",
    accessToken: "",
    subid: "",
    claims: {
      userHasAccessToApplication: false,
      canEditAssets: true,
      canViewNPD: false,
      canManageTaxonomies: false,
      canUploadLargeFiles: false,
    },
    user: null,
    sasToken: null,
  },

  actions: {
    test() {},
    callSilentRefreshOnUm() {},
    callLoginOnUm(context, returnUrl) {
      LogIn(returnUrl);
    },
    callSignInRedirectOnUM() {
      SigninRedirect();
    },
    async setLoginStateAsDev(context) {
      context.commit("logIn");
      const userName =
        process.env.VUE_APP_OFFLINE_MODE === "true"
          ? "OfflineUser"
          : "TestUser";
      context.commit("setUserName", userName);
      const fakeUser = {
        profile: {
          name: userName,
          role: "superUser",
          "ixp_markets.access": [],
        },
        access_token: "fake-dev-token",
      };
      context.commit("setUser", fakeUser);
      
      // Set all permissions for superuser
      context.commit("canEditAssets", true);
      context.commit("canViewNPD", true);
      context.commit("canManageTaxonomies", true);
      context.commit("canUploadLargeFiles", true);
    },
    async updateLoggedInStatus(context) {
      let user = await authService.getUser();

      if (user) {
        const profile = user.profile;

        // Process permissions from token profile
        context.commit("setSasToken", profile.sastoken);
        const canViewNPD = !!(
          profile["ixp_researchxp.access"] === "true" ||
          profile.role === "superUser"
        );
        context.commit("canViewNPD", canViewNPD);

        // OIDC storage can lag behind in-memory workspace JWT; keep Vuex aligned with /ping monitor.
        try {
          const crossSync = await import("../services/workspaceCrossAppSync");
          if (
            crossSync.getMonitorCurrentUser &&
            crossSync.shouldPreferMonitorOverLoadedUser &&
            crossSync.shouldPreferMonitorOverLoadedUser(user)
          ) {
            const mon = crossSync.getMonitorCurrentUser();
            if (mon && mon.access_token) {
              user = mon;
            }
          }
        } catch (e) {
          /* ignore */
        }

        context.commit("setUser", user);
        context.commit("logIn");
        context.commit("setUserName", profile.name);
        context.commit("setAccessToken", user.access_token);
        context.commit(
          "setsubid",
          profile.username || profile.sub || profile.preferred_username
        );

        // Trigger market permissions update after user is set
        context.dispatch("updateMarketPermissions");
        return;
      }

      context.commit("logOut");
      context.commit("setUserName", "");
    },
    updateMarketPermissions({ commit, state, rootState }) {
      // Recalculate permissions when market selection changes
      const selectedMarketId = rootState.assetxpstate.selectedMarketId;
      const profile = state.user?.profile;

      if (!profile) return;

      // Check if user is super admin or market admin
      const isMarketAdmin = !!(
        profile.role === "superUser" ||
        (selectedMarketId &&
          profile[`ixp_markets.${selectedMarketId}.admin`] === "true")
      );

      // Update dependent permissions
      const canManageTaxonomies = !!(
        profile.role === "superUser" || isMarketAdmin
      );
      const canUploadLargeFiles = !!(
        profile.role === "superUser" || isMarketAdmin
      );

      commit("canManageTaxonomies", canManageTaxonomies);
      commit("canUploadLargeFiles", canUploadLargeFiles);
    },
  },
  mutations: {
    setSasToken(state, sasToken) {
      state.sasToken = sasToken;
    },
    setUser(state, user) {
      state.user = user;
    },
    logIn(state) {
      state.loggedIn = true;
    },
    logOut(state) {
      state.loggedIn = false;
    },
    setsubid(state, subid) {
      state.subid = subid;
    },
    setUserName(state, userName) {
      state.userName = userName;
    },
    setAccessToken(state, accessToken) {
      var now = new Date();
      var time = now.getTime();
      var expireTime = time + 1000 * (60 * 1380);
      now.setTime(expireTime);
      const expires = "expires=" + now.toUTCString();

      var cookieVal = getCookieValue("idsrv.session");

      if (cookieVal != null) {
        document.cookie =
          "idsrv.session=" + cookieVal + "; " + expires + ";secure;";
      }

      state.accessToken = accessToken;
    },
    userHasAccessToApplication(state, hasAccess) {
      state.claims.userHasAccessToApplication = hasAccess;
    },
    canEditAssets(state, hasAccess) {
      state.claims.canEditAssets = hasAccess;
    },
    canViewNPD(state, hasAccess) {
      state.claims.canViewNPD = hasAccess;
    },
    canManageTaxonomies(state, hasAccess) {
      state.claims.canManageTaxonomies = hasAccess;
    },
    canUploadLargeFiles(state, hasAccess) {
      state.claims.canUploadLargeFiles = hasAccess;
    },
  },
  getters: {
    sasToken: (state) => {
      return state.sasToken;
    },
    isMarketAdmin: (state, getters, rootState) => {
      const selectedMarketId = rootState.assetxpstate.selectedMarketId;
      const profile = state.user?.profile;
      const accessPayload = parseJwtPayload(state.user?.access_token);

      if (!profile) return false;

      // Check if user is super admin or market admin
      return !!(
        profile.role === "superUser" ||
        (selectedMarketId &&
          (profile[`ixp_markets.${selectedMarketId}.admin`] === "true" ||
            claimContainsMarketId(profile["ixp_market_admin"], selectedMarketId) ||
            claimContainsMarketId(accessPayload && accessPayload.ixp_market_admin, selectedMarketId)))
      );
    },
    CanUserEditAssets: (state, getters, rootState, rootGetters) => {
      const profile = state.user?.profile;
      if (!profile) {
        return false;
      }

      const accessPayload = parseJwtPayload(state.user?.access_token);

      const effectiveMarketId =
        rootGetters["assetxpstate/effectiveMarketId"] || null;

      // Never grant edit rights against a stale market id when markets are already resolved.
      const markets = Array.isArray(rootState.assetxpstate?.markets)
        ? rootState.assetxpstate.markets
        : [];
      const availableMarketIds = markets
        .map((m) => (m && m.id ? String(m.id).trim() : null))
        .filter(Boolean);

      if (
        availableMarketIds.length > 0 &&
        (!effectiveMarketId || !availableMarketIds.includes(String(effectiveMarketId).trim()))
      ) {
        return false;
      }

      if (!effectiveMarketId) {
        return false;
      }

      if (availableMarketIds.length === 0) {
        // Fail closed while markets are unresolved to avoid stale-token edit affordances.
        return false;
      }

      // Market-specific admin permission (id_token / profile)
      const marketAdminKey = `ixp_markets.${effectiveMarketId}.admin`;
      if (profile[marketAdminKey] === "true") {
        return true;
      }

      if (claimContainsMarketId(profile["ixp_market_admin"], effectiveMarketId)) {
        return true;
      }

      if (claimContainsMarketId(accessPayload && accessPayload.ixp_market_admin, effectiveMarketId)) {
        return true;
      }

      return false;
    },
    /**
     * Add / Update Assets nav + page (import/export, etc.).
     * Same bar as editing assets: super admin or market admin only (see CanUserEditAssets).
     */
    CanUserAccessAddUpdateAssets: (state, getters) => {
      return getters.CanUserEditAssets;
    },
    CanUserAccessCleanAttributeValues: (state) => {
      const profile = state.user?.profile;
      if (!profile) {
        return false;
      }

      const allowedRoles = ["ProductOwner", "superUser"];

      if (allowedRoles.some((r) => claimContainsRole(profile.role, r))) {
        return true;
      }

      const accessPayload = parseJwtPayload(state.user?.access_token);
      if (allowedRoles.some((r) => claimContainsRole(accessPayload && accessPayload.role, r))) {
        return true;
      }

      // Impersonation sessions carry an "act" claim only when IdentityServer's token-exchange
      // grant validated the acting admin as a productOwner (see TokenExchangeGrantValidator),
      // so its presence is itself proof of productOwner-initiated impersonation.
      return Boolean(profile.act || (accessPayload && accessPayload.act));
    },
    /**
     * Product Search Admin (rebuild the search index for the caller's tenant). The backend endpoint
     * (AssetXpApi's ProductSearchController.ReindexCurrentTenant) is gated by the "ProductOwner"
     * authorization policy only - unlike CanUserAccessCleanAttributeValues, this does NOT also allow
     * superUser, so the button is never shown to a user whose role would get a 403 from the API.
     *
     * TEMPORARY: the role check below is bypassed when running the local dev server
     * (`npm run serve`, NODE_ENV=development) to make manual testing easier without needing a
     * ProductOwner-flagged test account. This is NOT a security hole - the backend endpoint still
     * enforces the ProductOwner policy independently of anything the frontend shows, so a
     * non-ProductOwner clicking the button locally just gets a 403. It also can't leak into a real
     * build: `vue-cli-service build` always sets NODE_ENV=production, so this never ships enabled.
     * Remove this block once ProductOwner test accounts are easier to come by locally.
     */
    CanUserAccessProductSearchReindex: (state) => {
      if (process.env.NODE_ENV === "development") {
        return true;
      }

      const profile = state.user?.profile;
      if (!profile) {
        return false;
      }

      if (claimContainsRole(profile.role, "ProductOwner")) {
        return true;
      }

      const accessPayload = parseJwtPayload(state.user?.access_token);
      if (claimContainsRole(accessPayload && accessPayload.role, "ProductOwner")) {
        return true;
      }

      // Impersonation sessions carry an "act" claim only when IdentityServer's token-exchange
      // grant validated the acting admin as a productOwner (see TokenExchangeGrantValidator),
      // so its presence is itself proof of productOwner-initiated impersonation - a user being
      // impersonated by a ProductOwner must see this button too.
      return Boolean(profile.act || (accessPayload && accessPayload.act));
    },
    GetAuthHeaderObj: (state) => {
      // Prefer token on user object (oidc user) — matches UserManager; root accessToken can lag briefly.
      var token =
        state.user && state.user.access_token
          ? state.user.access_token
          : state.accessToken;
      return { Authorization: "Bearer " + token };
    },
    availableTargetMarkets: (state, getters, rootState) => {
      const markets = rootState.assetxpstate.markets;
      const currentMarketId = rootState.assetxpstate.selectedMarketId;

      if (!markets || !state.user?.profile) {
        return [];
      }

      const userProfile = state.user.profile;
      const isSuperUser = userProfile.role === "superUser";

      return markets.filter((market) => {
        // Can't clone to the same market
        if (market.id === currentMarketId) {
          return false;
        }

        // Super users can clone to any market they have access to
        if (isSuperUser) {
          const marketAccess = userProfile["ixp_markets.access"] || [];
          return marketAccess.includes(market.id);
        }

        // Regular users can only clone to markets where they are admin
        const marketAdminKey = `ixp_markets.${market.id}.admin`;
        return userProfile[marketAdminKey] === "true";
      });
    },
    hasAvailableTargetMarkets: (state, getters) => {
      return getters.availableTargetMarkets.length > 0;
    },
    hasMarketAccess: (state) => {
      // Check if the user has market-based access control (key exists)
      return !!state.user?.profile?.["ixp_markets.access"];
    },
    defaultMarketId: (state) => {
      return state.user?.profile?.["ixp_markets.defaultmarket"] || null;
    },
  },
};
